import { useEffect, useState } from 'react';
import { SYMBOL_SETS, SymbolSetKey } from '@/modules/calendar/model/data/data-impact-symbol-sets';
import { ChartDataResponse } from '@/modules/calendar/model/data/data-impact-chart';
import { impactApi } from '@/modules/calendar/api';

// Module-level cache so multiple components/hooks can share cached data
const chartDataCache = new Map<string, ChartDataResponse | null>();

export function useChartData(
  eventTimestampMs: number | undefined,
  session: 'regular' | 'extended' | null,
  symbolSet: SymbolSetKey,
) {
  const [data, setData] = useState<ChartDataResponse | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!eventTimestampMs) return;

    const cacheKey = `${symbolSet}:${eventTimestampMs}`;

    // Check cache first
    if (chartDataCache.has(cacheKey)) {
      setData(chartDataCache.get(cacheKey));
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(undefined);
    setData(undefined);

    const symbols = SYMBOL_SETS[symbolSet].symbols;

    impactApi
      .fetchChartData(
        {
          eventTimestampMs,
          session,
          symbols,
          bucketsPerPage: 300,
          symbolsLimit: 5,
        },
        controller.signal,
      )
      .then(json => {
        if (cancelled) return;
        chartDataCache.set(cacheKey, json);
        setData(json);
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        if (e?.message === 'Fetch cancelled') return;
        setError(e?.message || 'Failed to load chart data');
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [eventTimestampMs, session, symbolSet]);

  return { data, loading, error } as const;
}
